import React from 'react';
import { Check, Camera, Zap } from 'lucide-react';

const checks = {
  en: [
    'Complete nutrient analysis in seconds',
    'Track vitamins, minerals and macros',
    'AI wellbeing score for every meal',
  ],
  de: [
    'Vollständige Nährstoffanalyse in Sekunden',
    'Verfolge Vitamine, Mineralstoffe und Makros',
    'KI-Wohlbefindens-Score für jede Mahlzeit',
  ],
};

export default function FoodScanner({ lang }) {
  const t = {
    label: lang === 'de' ? 'KI Mahlzeitenanalyse' : 'AI Meal Analysis',
    headline:
      lang === 'de'
        ? 'KI-MAHLZEITENANALYSE FÜR WOHLBEFINDEN'
        : 'AI MEAL ANALYSIS FOR WELLBEING',
    sub:
      lang === 'de'
        ? 'Fotografiere dein Essen — die KI bewertet nicht nur Kalorien, sondern deine gesamte Nährstoffbalance.'
        : 'Photograph your food — AI rates not just calories but your complete nutrient balance.',
    cta: lang === 'de' ? 'Mahlzeit analysieren' : 'Analyse meal',
  };

  return (
    <section className="bg-white py-20 lg:py-28 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left — image */}
          <div className="relative flex justify-center">
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-lime-100/60 to-emerald-100/40 rounded-3xl blur-2xl" />
            <img
              src="https://images.unsplash.com/photo-1544991875-5dc1b05f1054?w=800&q=80"
              alt="AI food scanner"
              className="w-full max-w-md h-[380px] lg:h-[460px] object-cover rounded-3xl shadow-2xl"
            />

            {/* Floating scan animation badge */}
            <div className="absolute top-6 right-4 lg:-right-4 bg-white rounded-2xl px-4 py-3 shadow-xl border border-slate-100 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-lime-400 flex items-center justify-center animate-pulse">
                <Zap size={14} color="white" fill="white" />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-medium leading-none mb-0.5">
                  {lang === 'de' ? 'Analyse' : 'Analysis'}
                </p>
                <p className="text-sm font-bold text-slate-800">
                  {lang === 'de' ? '382 kcal' : '382 kcal'}
                </p>
              </div>
            </div>

            {/* Score badge */}
            <div className="absolute bottom-6 left-4 lg:-left-4 bg-emerald-600 text-white rounded-2xl px-5 py-3 shadow-xl">
              <p className="text-xs font-medium opacity-80 uppercase tracking-wide mb-0.5">
                {lang === 'de' ? 'Score' : 'Score'}
              </p>
              <p className="text-2xl font-black">9.2 / 10</p>
            </div>
          </div>

          {/* Right — text */}
          <div className="flex flex-col gap-6">
            {/* Label */}
            <span className="inline-flex items-center gap-2 w-fit px-4 py-1.5 bg-slate-100 text-slate-700 text-xs font-bold uppercase tracking-widest rounded-full">
              <Camera size={12} />
              {t.label}
            </span>

            <h2 className="text-3xl sm:text-4xl lg:text-4xl xl:text-5xl font-black text-slate-900 tracking-tight leading-tight">
              {t.headline}
            </h2>

            <p className="text-slate-500 text-lg leading-relaxed">{t.sub}</p>

            {/* Checkmarks */}
            <ul className="flex flex-col gap-4 mt-2">
              {checks[lang].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center mt-0.5">
                    <Check size={14} color="white" strokeWidth={3} />
                  </div>
                  <span className="text-slate-700 font-medium leading-snug">{item}</span>
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="pt-2">
              <a
                href="/s"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md hover:shadow-lg hover:shadow-emerald-200 transition-all duration-300 hover:-translate-y-0.5"
              >
                <Camera size={18} />
                {t.cta}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
